import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Menu, X, Phone, ChevronDown } from 'lucide-react'
import { NAV_ITEMS } from '@/data/navigation'
import { SITE } from '@/data/site'
import { cn } from '@/utils/cn'

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled,   setScrolled]   = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setOpenDropdown(null)
  }, [location.pathname])

  return (
    <header className={cn(
      'fixed inset-x-0 top-0 z-50 transition-all duration-300',
      scrolled ? 'bg-secondary-900/95 backdrop-blur-sm shadow-lg' : 'bg-secondary-900/80 backdrop-blur-sm',
    )}>
      {/* Top bar */}
      <div className="hidden lg:block bg-secondary-950/60 border-b border-white/10">
        <div className="container-content flex items-center justify-between py-1.5 text-xs text-secondary-200">
          <span>Self-Catering in the Heart of Snowdonia — Open All Year</span>
          <a href={SITE.phoneTel} className="flex items-center gap-1.5 hover:text-accent-400 transition-colors font-medium">
            <Phone size={12} />
            {SITE.phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="container-content">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link to="/" className="flex flex-col leading-tight" aria-label="Coed y Celyn Hall — Home">
            <span className="font-heading text-white font-bold text-lg leading-tight">Coed y Celyn Hall</span>
            <span className="text-accent-400 text-xs font-medium tracking-wide">Betws-y-Coed · Snowdonia</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
            {NAV_ITEMS.map((item) =>
              item.children ? (
                <div
                  key={item.href}
                  className="relative group"
                  onMouseEnter={() => setOpenDropdown(item.href)}
                  onMouseLeave={() => setOpenDropdown(null)}
                >
                  <button className={cn('nav-link flex items-center gap-1 px-3 py-2 rounded-md')}>
                    {item.label}
                    <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                  </button>
                  {openDropdown === item.href && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                      {item.children.map((child) => (
                        <NavLink
                          key={child.href}
                          to={child.href}
                          className={({ isActive }) => cn(
                            'block px-4 py-2.5 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-700 transition-colors',
                            isActive && 'text-primary-700 font-semibold bg-primary-50',
                          )}
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) => cn(
                    'nav-link px-3 py-2 rounded-md',
                    isActive && 'nav-link-active',
                  )}
                  end={item.href === '/'}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* CTA + mobile toggle */}
          <div className="flex items-center gap-3">
            <a href="/enquire" className="hidden sm:inline-flex btn-accent btn-sm">
              Enquire Now
            </a>
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-secondary-900 border-t border-white/10 pb-4">
          <nav className="container-content flex flex-col pt-2" aria-label="Mobile navigation">
            {NAV_ITEMS.map((item) => (
              <div key={item.href}>
                <NavLink
                  to={item.href}
                  className={({ isActive }) => cn(
                    'block py-3 border-b border-white/10 text-secondary-100 hover:text-accent-400 font-medium',
                    isActive && 'text-accent-400',
                  )}
                  end={item.href === '/'}
                >
                  {item.label}
                </NavLink>
                {item.children?.map((child) => (
                  <NavLink
                    key={child.href}
                    to={child.href}
                    className={({ isActive }) => cn(
                      'block py-2.5 pl-4 border-b border-white/5 text-sm text-secondary-300 hover:text-accent-400',
                      isActive && 'text-accent-400 font-medium',
                    )}
                  >
                    {child.label}
                  </NavLink>
                ))}
              </div>
            ))}
            <div className="flex flex-col gap-3 pt-4">
              <a href="/enquire" className="btn-accent text-center py-3">Enquire Now</a>
              <a href={SITE.phoneTel} className="flex items-center justify-center gap-2 text-secondary-200 hover:text-accent-400">
                <Phone size={16} />
                <span className="font-medium">{SITE.phone}</span>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
